import { Body1Strong, Card, Caption1, makeStyles, mergeClasses, Title1, tokens } from '@fluentui/react-components';
import type { ReactNode } from 'react';

import { DASHBOARD_CARD_APPEARANCE_STYLE } from '../dashboard.styles';
import type { TDashboardMetric, TDashboardTone } from '../types/dashboard.types';

const useStyles = makeStyles({
  card: {
    ...DASHBOARD_CARD_APPEARANCE_STYLE,
    minHeight: '136px',
    padding: `${tokens.spacingVerticalL} ${tokens.spacingHorizontalL}`,
  },
  interactive: {
    cursor: 'pointer',
    transitionProperty: 'background-color, box-shadow',
    transitionDuration: '120ms',
    ':hover': {
      boxShadow: tokens.shadow4,
    },
    ':focus-visible': {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: '2px',
    },
  },
  selected: {
    backgroundColor: tokens.colorNeutralBackground1Selected,
  },
  content: {
    display: 'flex',
    height: '100%',
    flexDirection: 'column',
    justifyContent: 'space-between',
    gap: tokens.spacingVerticalL,
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
  },
  iconBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '36px',
    height: '36px',
    minWidth: '36px',
    borderRadius: tokens.borderRadiusMedium,
  },
  title: {
    color: tokens.colorNeutralForeground2,
    lineHeight: tokens.lineHeightBase300,
  },
  value: {
    display: 'block',
    color: tokens.colorNeutralForeground1,
    fontSize: '34px',
    lineHeight: '38px',
    fontWeight: tokens.fontWeightSemibold,
  },
  caption: {
    color: tokens.colorNeutralForeground3,
  },
  danger: {
    borderTop: `4px solid ${tokens.colorPaletteRedBorderActive}`,
  },
  warning: {
    borderTop: `4px solid ${tokens.colorPaletteMarigoldBorderActive}`,
  },
  success: {
    borderTop: `4px solid ${tokens.colorPaletteGreenBorderActive}`,
  },
  info: {
    borderTop: `4px solid ${tokens.colorPaletteBlueBorderActive}`,
  },
  neutral: {
    borderTop: `4px solid ${tokens.colorNeutralStrokeAccessible}`,
  },
  dangerIcon: {
    color: tokens.colorPaletteRedForeground1,
    backgroundColor: tokens.colorPaletteRedBackground1,
  },
  warningIcon: {
    color: tokens.colorPaletteMarigoldForeground1,
    backgroundColor: tokens.colorPaletteMarigoldBackground1,
  },
  successIcon: {
    color: tokens.colorPaletteGreenForeground1,
    backgroundColor: tokens.colorPaletteGreenBackground1,
  },
  infoIcon: {
    color: tokens.colorPaletteBlueForeground2,
    backgroundColor: tokens.colorPaletteBlueBackground2,
  },
  neutralIcon: {
    color: tokens.colorNeutralForeground2,
    backgroundColor: tokens.colorNeutralBackground3,
  },
});

const getToneClassNames = (
  classes: ReturnType<typeof useStyles>
): Record<TDashboardTone, { card: string; icon: string }> => ({
  danger: { card: classes.danger, icon: classes.dangerIcon },
  warning: { card: classes.warning, icon: classes.warningIcon },
  success: { card: classes.success, icon: classes.successIcon },
  info: { card: classes.info, icon: classes.infoIcon },
  neutral: { card: classes.neutral, icon: classes.neutralIcon },
});

type TProps = {
  metric: TDashboardMetric;
  icon: ReactNode;
  controlsId?: string;
  isSelected?: boolean;
  onClick?: () => void;
};

const DashboardMetricCard = ({ metric, icon, controlsId, isSelected = false, onClick }: TProps) => {
  const classes = useStyles();
  const toneClassNames = getToneClassNames(classes)[metric.tone];
  const isInteractive = Boolean(onClick);

  return (
    <Card
      className={mergeClasses(
        classes.card,
        toneClassNames.card,
        isInteractive && classes.interactive,
        isSelected && classes.selected
      )}
      onClick={onClick}
      role={isInteractive ? 'button' : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      aria-controls={isInteractive ? controlsId : undefined}
      aria-pressed={isInteractive ? isSelected : undefined}
      onKeyDown={
        isInteractive
          ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
    >
      <div className={classes.content}>
        <div className={classes.header}>
          <Body1Strong className={classes.title}>{metric.title}</Body1Strong>
          <span className={mergeClasses(classes.iconBox, toneClassNames.icon)}>{icon}</span>
        </div>
        <div>
          <Title1 as="span" className={classes.value}>
            {metric.value.toLocaleString()}
          </Title1>
          <Caption1 className={classes.caption}>{metric.caption}</Caption1>
        </div>
      </div>
    </Card>
  );
};

export default DashboardMetricCard;
